import { computed, effect, EnvironmentInjector, inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, delay, EMPTY, filter, firstValueFrom, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HTTP } from 'src/app/services/http';
import { ActivatedRoute, NavigationEnd, ResolveFn, Router } from '@angular/router';
import { io, Socket } from 'socket.io-client';
import { NotificationService, ServerNotification } from './notification.service';

@Injectable({
    providedIn: 'root'
})
export class AppService {
    private socket: Socket | null = null;
    private oauth = inject(OAuthService);
    private notificationService = inject(NotificationService);
    private environmentInjector = inject(EnvironmentInjector);
    private router = inject(Router);
    private translateService = inject(TranslateService);
    private titleService = inject(Title);
    private last_title = signal<string | undefined>(undefined);
    public readonly pageName = computed(() => {
        const title = this.last_title() ?? ''; 
        if (!title.length) return ''
        return this.translateService.instant(title);
    });
    public readonly title = computed(() => {
        const pageName = this.pageName();
        const prefix = environment.production ? '' : 'DEV';
        const parts = [prefix, pageName, environment.TITLE].filter(part => part && part.length > 0);
        return parts.join(' - ');
    });

    public routeData = toSignal(this.router.events.pipe(filter(event => event instanceof NavigationEnd), map(() => {
        let route = this.router.routerState.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route.snapshot.data;
    })), { initialValue: {} });

    public language = toSignal(this.translateService.onLangChange.pipe(map(event => event.lang)), { initialValue: this.translateService.getCurrentLang() });
    
    onTitleChange = effect(() => {
        this.titleService.setTitle(this.title());
    });

    public setTitle(key?: string) {
        this.last_title.set(key);
    }
    public translate = this.translateService.instant.bind(this.translateService);
    
    constructor() {
		this.initSocket().catch(err => console.error('Error initializing Socket.IO:', err));
    }
    /**
    * Initialize Socket.IO connection for real-time notifications.
    *
    * The access token is what scopes the connection: the backend verifies it during the handshake and
    * joins this socket to its studio's room, so `notification` only ever carries our own studio's
    * events. Passing `auth` as a callback rather than an object matters - socket.io re-invokes it on
    * every reconnect, and an object would pin the token captured at startup and fail to re-authenticate
    * once it expired, leaving a connected socket in no room at all.
    */
    private async initSocket() {

        console.log('[NotificationService] Initializing Socket.IO connection on ' + environment.apiUrl + '...');
        // Awaited so the socket is only opened once a token is available to hand to the handshake.
        const studioID = await this.studioID();
        this.socket = io(environment.apiUrl, {
            auth: (cb) => cb({ token: this.oauth.getAccessToken() }),
        });
        this.socket.on('connect', () => {
            console.log(`[NotificationService] Connected to server for studio ${studioID}`);
            HTTP.get<ServerNotification[]>('notifications').then(notifications => {
                NotificationService._notifications.set(notifications);
            });
        });
        this.socket.on('connected', (event: {authenticated?: boolean}) => {
            if (event?.authenticated === false) {
                // Connected but in no room: every notification will be missed until a reconnect
                // re-authenticates, so make that visible instead of looking like an idle system.
                console.warn('[NotificationService] Socket.IO connected unauthenticated - no live notifications');
            }
        });
        this.socket.on('notification', (event: any) => this.notificationService.handleEvent(event));
        this.socket.on('disconnect', () => console.log('[NotificationService] Socket.IO disconnected'));
    }
    
    loggedIn() { return this.oauth.hasValidAccessToken(); }
    
    loggedIn$(): Observable<boolean> {
        return of(this.loggedIn()).pipe(delay(0)); // Wrap sync value in observable
    }
    
    logout() {
        this.oauth.logOut(); // kehrt nach postLogoutRedirectUri zurück
    }
    
    studio = signal<any>(undefined);
    
    async studioID() {
        return (await HTTP.get<{ studio: any }>('studio')).studio.id;
    }
    
    async getSetting<T>(key: string): Promise<T | undefined> {
        const request = await HTTP.get<{value: T}>(`studio/setting/${key}`);
        return request.value;
    }
    
    async hasSetting(key: string): Promise<boolean> {
        const request = await HTTP.get<{value: boolean}>(`studio/setting/${key}/exist`);
        return request.value;
    }
    
    async setSetting<T>(key: string, value: T): Promise<T | undefined> {
        const request = await HTTP.put<{value: T}>(`studio/setting/${key}`, { value });
        return request.value;
    }
    
    async deleteSetting(key: string): Promise<void> {
        await HTTP.delete(`studio/setting/${key}`);
    }
    public async reRunResolvers(activatedRoute: ActivatedRoute, environmentInjector?: EnvironmentInjector) {
        let result: Record<string, unknown> = {};
        for (const route of activatedRoute.pathFromRoot) {
            if (route.routeConfig?.data)
                result = { ...result, ...route.routeConfig.data };
            if (route.routeConfig?.resolve) {
                await Promise.all(
                    Object.entries(route.routeConfig.resolve).map(async ([key, resolver]) => {
                        let instance: {
                            resolve: ResolveFn<unknown>;
                        } | undefined =  undefined;;
                        if (typeof resolver.prototype?.constructor?.name === 'string') {
                            const injector = environmentInjector ?? Injector.create({ providers: [resolver], parent: this.environmentInjector });
                            instance = injector.get(resolver, undefined, { optional: true, skipSelf: true })!;
                        }
                        const method = instance?.resolve ?? resolver;
                        const res = runInInjectionContext(
                            (environmentInjector ?? this.environmentInjector),
                            () => method.apply(instance, [activatedRoute.snapshot, this.router.routerState.snapshot, true])
                        );
                        if (res === EMPTY)
                            throw new Error('Error while running ' + key + ' in route ' + route.routeConfig);
                        else if (res === undefined)
                            return; // Ignore undefined results
                        else if (res instanceof Promise)
                            result[key] = await res;
                        else if (res instanceof Observable)
                            result[key] = await firstValueFrom(res);
                        else
                            result[key] = res;
                    })
                );
            }
        }
        if (Object.keys(result).length > 0)
            (activatedRoute.data as BehaviorSubject<unknown>).next(result);
    }
}